import type { DataType } from '@glueapp/graph-types';

// --- State Types ---

export interface UnpackSelection {
    listMode?: 'nested-list' | 'flat-list' | 'pick-item';
    pickedIndexes?: { [listAncestorPath: string]: number };
}

export type UnpackSelections = { [dotPath: string]: UnpackSelection };

export interface UnpackNodeState {
    selections: UnpackSelections;
    expanded: boolean;
}

// --- Path Extraction ---

export interface LeafPathInfo {
    dotPath: string;
    leafType: DataType;
    listAncestorPaths: string[];
    /** True when the path points at an object container or array container (not a primitive leaf) */
    isContainer?: boolean;
}

/**
 * Recursively walk a DataType tree and extract all leaf (non-object) paths.
 * Arrays are traversed into their subType; we track which path segments are arrays
 * so the caller knows how many list levels sit above each leaf.
 *
 * Backwards-compatible wrapper around extractAllPaths that filters out container paths.
 */
export function extractLeafPaths(dataType: DataType): LeafPathInfo[] {
    return extractAllPaths(dataType).filter(pathInfo => !pathInfo.isContainer);
}

/**
 * Walk a DataType tree and emit a path entry for every selectable node:
 * primitive leaves, leaf arrays (e.g. string[]), object containers, and array
 * containers (array of object). Container paths have isContainer = true and
 * use the same listAncestorPaths semantics — array containers list themselves
 * as the innermost ancestor so existing list-mode logic Just Works.
 */
export function extractAllPaths(dataType: DataType): LeafPathInfo[] {
    const results: LeafPathInfo[] = [];
    walkType(dataType, '', [], results, false);
    return results;
}

function walkType(
    dataType: DataType,
    currentPath: string,
    listAncestorPaths: string[],
    results: LeafPathInfo[],
    skipObjectContainerEmission: boolean,
): void {
    switch (dataType.kind) {
        case 'object': {
            // Emit object container path (not for the root, and not when we just
            // emitted the array container that wraps this object)
            if (currentPath !== '' && !skipObjectContainerEmission) {
                results.push({
                    dotPath: currentPath,
                    leafType: dataType,
                    listAncestorPaths: [...listAncestorPaths],
                    isContainer: true,
                });
            }
            for (const [propName, propType] of Object.entries(dataType.properties)) {
                const childPath = currentPath ? `${currentPath}.${propName}` : propName;
                walkType(propType, childPath, listAncestorPaths, results, false);
            }
            break;
        }
        case 'array': {
            const newListAncestors = [...listAncestorPaths, currentPath];
            if (dataType.subType.kind === 'object') {
                // Emit the array container path (only when not at root)
                if (currentPath !== '') {
                    results.push({
                        dotPath: currentPath,
                        leafType: dataType.subType,
                        listAncestorPaths: newListAncestors,
                        isContainer: true,
                    });
                }
                // Recurse into the inner object — but skip emitting the same path
                // again as an object container (the array container already covers it)
                walkType(dataType.subType, currentPath, newListAncestors, results, true);
            } else {
                // Leaf array (e.g. string[]) — the current path IS the leaf
                results.push({
                    dotPath: currentPath,
                    leafType: dataType.subType,
                    listAncestorPaths: newListAncestors,
                });
            }
            break;
        }
        default: {
            // Primitive leaf (string, number, boolean, etc.)
            results.push({
                dotPath: currentPath,
                leafType: dataType,
                listAncestorPaths: [...listAncestorPaths],
            });
            break;
        }
    }
}

// --- Default Selections ---

/**
 * Compute the default selections for a given input type.
 * All leaf properties are enabled. Properties with list ancestors default to flat-list.
 */
export function computeDefaultSelections(dataType: DataType): UnpackSelections {
    const paths = extractLeafPaths(dataType);
    const selections: UnpackSelections = {};
    for (const pathInfo of paths) {
        if (pathInfo.listAncestorPaths.length > 0) {
            selections[pathInfo.dotPath] = { listMode: 'flat-list' };
        } else {
            selections[pathInfo.dotPath] = {};
        }
    }
    return selections;
}

// --- Merge Selections on Type Change ---

/**
 * When the input type changes, preserve selections for paths that still exist,
 * remove stale ones, and add defaults for new leaf paths. Container selections
 * are preserved when their path still exists in the new type but are not added
 * to defaults (containers are opt-in).
 */
export function mergeSelectionsOnTypeChange(oldSelections: UnpackSelections, newType: DataType): UnpackSelections {
    const newDefaults = computeDefaultSelections(newType);
    const merged: UnpackSelections = {};

    for (const [dotPath, defaultSelection] of Object.entries(newDefaults)) {
        if (dotPath in oldSelections) {
            merged[dotPath] = oldSelections[dotPath];
        } else {
            merged[dotPath] = defaultSelection;
        }
    }

    // Preserve container selections that survive the type change
    const newPathSet = new Set(extractAllPaths(newType).map(pathInfo => pathInfo.dotPath));
    for (const [dotPath, selection] of Object.entries(oldSelections)) {
        if (!(dotPath in merged) && newPathSet.has(dotPath)) {
            merged[dotPath] = selection;
        }
    }

    return merged;
}

// --- Output Type Computation ---

/**
 * Compute the output DataType for a selected path, given its leaf type,
 * list ancestor count, and the selection config.
 */
export function computeOutputTypeForPath(leafType: DataType, listAncestorPaths: string[], selection: UnpackSelection): DataType {
    const listLevels = listAncestorPaths.length;

    if (listLevels === 0 || !selection.listMode) {
        return leafType;
    }

    switch (selection.listMode) {
        case 'flat-list':
            return { kind: 'array', subType: leafType };

        case 'nested-list': {
            let result: DataType = leafType;
            for (let index = 0; index < listLevels; index++) {
                result = { kind: 'array', subType: result };
            }
            return result;
        }

        case 'pick-item':
            return leafType;
    }
}

// --- Port Name Disambiguation ---

/**
 * Given a list of dot-paths, compute display names using leaf-only naming
 * with parent disambiguation when conflicts occur.
 */
export function disambiguatePortNames(dotPaths: string[]): { [dotPath: string]: string } {
    const result: { [dotPath: string]: string } = {};

    // Start with leaf names
    const leafNames: { [dotPath: string]: string } = {};
    for (const dotPath of dotPaths) {
        const segments = dotPath.split('.');
        leafNames[dotPath] = segments[segments.length - 1];
    }

    // Find conflicts and resolve them by adding parent segments
    const nameGroups = new Map<string, string[]>();
    for (const [dotPath, name] of Object.entries(leafNames)) {
        if (!nameGroups.has(name)) {
            nameGroups.set(name, []);
        }
        nameGroups.get(name)!.push(dotPath);
    }

    for (const [, paths] of nameGroups) {
        if (paths.length === 1) {
            result[paths[0]] = leafNames[paths[0]];
        } else {
            // Expand progressively until all names in this group are unique
            resolveConflicts(paths, result);
        }
    }

    return result;
}

function resolveConflicts(conflictingPaths: string[], result: { [dotPath: string]: string }): void {
    // Try progressively longer suffixes until names are unique
    for (let suffixLength = 2; suffixLength <= 20; suffixLength++) {
        const candidateNames: { [dotPath: string]: string } = {};
        let allUnique = true;

        for (const dotPath of conflictingPaths) {
            const segments = dotPath.split('.');
            const start = Math.max(0, segments.length - suffixLength);
            candidateNames[dotPath] = segments.slice(start).join('.');
        }

        // Check uniqueness
        const seen = new Set<string>();
        for (const name of Object.values(candidateNames)) {
            if (seen.has(name)) {
                allUnique = false;
                break;
            }
            seen.add(name);
        }

        if (allUnique) {
            for (const [dotPath, name] of Object.entries(candidateNames)) {
                result[dotPath] = name;
            }
            return;
        }
    }

    // Fallback: use full path
    for (const dotPath of conflictingPaths) {
        result[dotPath] = dotPath;
    }
}

// --- Value Extraction ---

/**
 * Extract a value from an input object/array following a dot-path,
 * applying list mode logic at each array boundary.
 */
export function extractValueAtPath(input: any, dotPath: string, selection: UnpackSelection, listAncestorPaths: string[]): any {
    if (listAncestorPaths.length === 0 || !selection.listMode) {
        return getNestedValue(input, dotPath);
    }

    switch (selection.listMode) {
        case 'flat-list':
            return collectFlat(input, dotPath.split('.'), 0, '', listAncestorPaths);

        case 'nested-list':
            return collectNested(input, dotPath.split('.'), 0, '', listAncestorPaths);

        case 'pick-item':
            return pickItem(input, dotPath.split('.'), 0, '', listAncestorPaths, selection.pickedIndexes || {});
    }
}

function getNestedValue(object: any, dotPath: string): any {
    const segments = dotPath.split('.');
    let current = object;
    for (const segment of segments) {
        if (current == null) return null;
        current = current[segment];
    }
    return current ?? null;
}

function collectFlat(current: any, segments: string[], segmentIndex: number, currentPath: string, listAncestorPaths: string[]): any[] {
    if (current == null) return [];

    // Check if current position is an array boundary
    if (Array.isArray(current) && listAncestorPaths.includes(currentPath)) {
        const results: any[] = [];
        for (const item of current) {
            const sub = collectFlat(item, segments, segmentIndex, currentPath, listAncestorPaths);
            if (Array.isArray(sub)) {
                results.push(...sub);
            } else {
                results.push(sub);
            }
        }
        return results;
    }

    // Navigate deeper
    if (segmentIndex >= segments.length) {
        return Array.isArray(current) ? current : [current];
    }

    const nextSegment = segments[segmentIndex];
    const nextPath = currentPath ? `${currentPath}.${nextSegment}` : nextSegment;
    const nextValue = current[nextSegment];

    return collectFlat(nextValue, segments, segmentIndex + 1, nextPath, listAncestorPaths);
}

function collectNested(current: any, segments: string[], segmentIndex: number, currentPath: string, listAncestorPaths: string[]): any {
    if (current == null) return null;

    // Check if current position is an array boundary
    if (Array.isArray(current) && listAncestorPaths.includes(currentPath)) {
        return current.map(item => collectNested(item, segments, segmentIndex, currentPath, listAncestorPaths));
    }

    // Navigate deeper
    if (segmentIndex >= segments.length) {
        return current;
    }

    const nextSegment = segments[segmentIndex];
    const nextPath = currentPath ? `${currentPath}.${nextSegment}` : nextSegment;
    const nextValue = current[nextSegment];

    return collectNested(nextValue, segments, segmentIndex + 1, nextPath, listAncestorPaths);
}

function pickItem(
    current: any,
    segments: string[],
    segmentIndex: number,
    currentPath: string,
    listAncestorPaths: string[],
    pickedIndexes: { [path: string]: number },
): any {
    if (current == null) return null;

    // Check if current position is an array boundary
    if (Array.isArray(current) && listAncestorPaths.includes(currentPath)) {
        const index = pickedIndexes[currentPath] ?? 0;
        if (index >= current.length) return null;
        return pickItem(current[index], segments, segmentIndex, currentPath, listAncestorPaths, pickedIndexes);
    }

    // Navigate deeper
    if (segmentIndex >= segments.length) {
        return current;
    }

    const nextSegment = segments[segmentIndex];
    const nextPath = currentPath ? `${currentPath}.${nextSegment}` : nextSegment;
    const nextValue = current[nextSegment];

    return pickItem(nextValue, segments, segmentIndex + 1, nextPath, listAncestorPaths, pickedIndexes);
}
